<template>
  <div class="app-wrapper">
    <div class="container">
      <section class="top-panel">
        <Header />
        <MainContent />
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch } from "vue";
import { useDashboardStore } from "@/stores/dashboard";
import Header from "@/components/Header.vue";
import MainContent from "@/components/MainContent.vue";

const dashboardStore = useDashboardStore();

onMounted(() => {
  // 背景画像の適用
  if (dashboardStore.backgroundImage) {
    applyBackgroundImage(dashboardStore.backgroundImage);
  }

  // レイアウト同期と初期クラス適用
  syncLayoutToViewport();
  document.body.classList.toggle("edit-mode-active", dashboardStore.isEditMode);
  document.body.classList.toggle(
    "gmail-sidebar-active",
    dashboardStore.isGmailSidebarOpen,
  );

  window.addEventListener("resize", syncLayoutToViewport);
  window.visualViewport?.addEventListener("resize", syncLayoutToViewport);
});

onUnmounted(() => {
  window.removeEventListener("resize", syncLayoutToViewport);
  window.visualViewport?.removeEventListener("resize", syncLayoutToViewport);
});

// 背景画像の変更を監視
watch(
  () => dashboardStore.backgroundImage,
  (newImage) => {
    if (newImage) {
      applyBackgroundImage(newImage);
    } else {
      removeBackgroundImage();
    }
  },
);

watch(
  () => dashboardStore.isEditMode,
  (isEditMode) => {
    document.body.classList.toggle("edit-mode-active", isEditMode);
  },
  { immediate: true },
);

watch(
  () => dashboardStore.isGmailSidebarOpen,
  (isOpen) => {
    document.body.classList.toggle("gmail-sidebar-active", isOpen);
  },
  { immediate: true },
);

function syncLayoutToViewport(): void {
  const root = document.documentElement;
  const headerElement = document.querySelector(".header") as HTMLElement | null;
  const footerWrapElement = document.querySelector(
    ".footer-wrap",
  ) as HTMLElement | null;

  const headerHeight = headerElement ? headerElement.offsetHeight : 0;
  const footerHeight = footerWrapElement ? footerWrapElement.offsetHeight : 0;
  const viewportWidth = Math.round(
    window.visualViewport?.width || window.innerWidth || 1024,
  );

  const responsiveContentWidth = Math.max(
    320,
    Math.min(700, Math.round(viewportWidth * 0.54)),
  );
  const responsiveShortcutMin = Math.max(
    68,
    Math.min(96, Math.round(responsiveContentWidth * 0.15)),
  );
  const responsiveShortcutGap = Math.max(
    8,
    Math.min(14, Math.round(responsiveContentWidth * 0.02)),
  );

  root.style.setProperty("--header-height", `${headerHeight}px`);
  root.style.setProperty("--footer-height", `${footerHeight}px`);
  root.style.setProperty("--content-width", `${responsiveContentWidth}px`);
  root.style.setProperty("--shortcut-min-size", `${responsiveShortcutMin}px`);
  root.style.setProperty("--shortcut-gap", `${responsiveShortcutGap}px`);
}

function applyBackgroundImage(imageData: string): void {
  document.body.style.backgroundImage = `url(${imageData})`;
  document.body.style.backgroundRepeat = "no-repeat";
  document.body.style.backgroundSize = "cover";
  document.body.style.backgroundPosition = "center center";
  document.body.classList.add("custom-bg");
}

function removeBackgroundImage(): void {
  document.body.classList.remove("custom-bg");
  document.body.style.removeProperty("background-image");
  document.body.style.removeProperty("background-repeat");
  document.body.style.removeProperty("background-size");
  document.body.style.removeProperty("background-position");
}
</script>

<style>
#app {
  width: 100%;
  height: 100%;
}
</style>
